<template>
  <div>
    <v-app-bar app clipped-left class="mt-16 sub-app-bar px-5">
      <v-row justify="space-around">
        <v-col>
          <p class="title">{{ $t('book/facility:overview.sub-bar-title') }}</p>
          <p class="subtitle">{{ $t('common:dashboard') }}</p>
        </v-col>
        <v-col cols="4" sm="5" md="4" lg="3" xl="2" align-self="center" class="text-right">
          <FacilityDialogForm ref="facilityDialogForm"/>
        </v-col>
      </v-row>
    </v-app-bar>

    <div class="px-9">
      <div class="d-sm-flex justify-space-between align-center">
        <v-btn color="greyBold" text @click="storeCardView(cardView = !cardView)">
          <v-icon left>list_alt</v-icon>
          {{ $t('book/facility:overview.dashboard.change-view') }}
        </v-btn>

        <v-radio-group v-model="toggleFacilities" row @change="updateTable(table.options, table.search)">
          <v-radio
              :label="$t('book/facility:overview.dashboard.assigned-facilities')"
              :value="own"
          />
          <v-radio
              :label="$t('book/facility:overview.dashboard.all-facilities')"
              :value="all"
          />
        </v-radio-group>
      </div>
    </div>

    <DashboardPaginated
        v-if="cardView"
        ref="facilityDashboard"
        @updateData="updateTable"
        @viewElement="viewFacility"
        @cardAction1="addDocumentation"
        @cardAction2="addContract"
        @cardAction3="addProcess"
        :itemCardBtn1Text="$t('common:documentation')"
        :itemCardBtn2Text="$t('common:contract')"
        :itemCardBtn3Text="$t('common:process')"
        :itemCardBtn1Tooltip="$t('book/facility:overview.dashboard.card-btn.docu')"
        :itemCardBtn2Tooltip="$t('book/facility:overview.dashboard.card-btn.contract')"
        :itemCardBtn3Tooltip="$t('book/facility:overview.dashboard.card-btn.process')"
        :itemCardViewIconTooltip="$t('book/facility:overview.dashboard.card-btn.view')"
        @cacheCommit="$store.commit('book/common/storeCommonSearch', $event)"
        :cachedSearch="$store.state.book.common.commonSearch"
    />

    <DataTablePaginated
        v-if="!cardView"
        ref="table"
        class="px-9"
        :headers="headers"
        checkbox-on-property="active"
        icon="create"
        :iconTooltip="$t('buttons:edit')"
        @clickedButton1="viewFacility"
        @updateTableData="updateTable"
        sortBy="name"
        @cacheCommit="$store.commit('book/common/storeCommonSearch', $event)"
        :cachedSearch="$store.state.book.common.commonSearch"
    />
  </div>
</template>

<script>
import FacilityService from "@/modules/book/services/facility-service";

import Utils from "@/modules/core/services/utils";

import DataTablePaginated from "@/modules/core/components/DataTable/DataTablePaginated";
import DashboardPaginated from "@/modules/core/components/DashboardPaginated";

import FacilityDialogForm from "./FacilityDialogForm";

export default {
  name: "FacilityOverview",
  components: {
    DataTablePaginated,
    DashboardPaginated,
    FacilityDialogForm
  },
  data() {
    return {
      table: {
        options: [],
        search: []
      },
      cardView: true,
      toggleFacilities: this.own,
      own: 1,
      all: 2
    };
  },
  methods: {
    viewFacility(item) {
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${item.id}/dashboard`);
    },
    addDocumentation(item) {
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${item.id}/info/documentations`);
    },
    addContract(item) {
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${item.id}/info/contracts`);
    },
    addProcess(item) {
      this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${item.id}/info/processes`);
    },

    fillInData(data) {
      data.content.forEach((facility) => {
        if (facility.typeIconUrl) {
          facility.typeIconUrl = process.env.VUE_APP_ROOT_HOST + facility.typeIconUrl;
        }
      })

      if(!this.cardView && this.$refs.table !== undefined) {
        this.$refs.table.fillTableData(data);
      } else if(this.cardView && this.$refs.facilityDashboard !== undefined) {
        this.$refs.facilityDashboard.fillDashboard(data)
      }
    },

    showFetchingError(error) {
      if (!this.cardView) {
        this.$refs.table.loading = false;
      }

      this.$logger.error("Error at fetching facilities: " + error);
      Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:facility_plural')}))
    },

    updateTable(options, search) {
      this.$store.commit('book/facility/storeToggleValue', this.toggleFacilities);

      this.table.options = options;
      this.table.search = search;

      if(!search) search = '';

      if(!this.cardView) {
        this.$refs.table.loading = true;
      }

      if (this.toggleFacilities === this.own) {
        FacilityService.readAllAssignedPaginatedFromAPI(
            options.page - 1,
            options.itemsPerPage,
            search,
            options.sortBy,
            options.sortDesc,
            (resp) => {
              this.fillInData(resp.data);
            },
            (error) => {
              this.showFetchingError(error);
            }
        )
      } else {
        FacilityService.readAllPaginatedFromAPI(
            options.page - 1,
            options.itemsPerPage,
            search,
            options.sortBy,
            options.sortDesc,
            (resp) => {
              this.fillInData(resp.data);
            },
            (error) => {
              this.showFetchingError(error);
            });
      }
    },

    storeCardView(item) {
      this.$store.commit('book/facility/storeCardViewState', item)
    }
  },
  computed: {
    headers() {
      return [
        {text: this.$t("common:name"), value: "name"},
        {text: this.$t("common:type"), value: "typeName"},
        {text: this.$t("common:building"), value: "buildingName"},
        {text: this.$t("common:city"), value: "buildingCity"},
        {text: this.$t("common:address"), value: "buildingStreet"},
        {text: this.$t("common:facility-number"), value: "objectNumber"},
        {text: this.$t("common:active"), value: "checkbox", sortable: false},
        {text: this.$t("common:settings"), value: "controls", sortable: false}
      ];
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if(to.query.building || to.query.openForm) {
        vm.$refs.facilityDialogForm.dialog = true;
      }
    });
  },
  mounted() {
    this.cardView = this.$store.state.book.facility.cardView;

    const toggleValue = this.$store.state.book.facility.toggleValue
    if (toggleValue) {
      this.toggleFacilities = toggleValue
    } else {
      this.toggleFacilities = this.$store.state.auth.user.roles.includes('ROLE_ADMIN')
          || this.$store.state.auth.user.roles.includes('ROLE_VIEW_EMPLOYEE')
          ? this.all : this.own
    }
  }
};
</script>